import moment from 'moment';
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { RadioGroupOption } from '../../common/RadioGroup/RadioGroup';
import { getAffectedSessions, getPauseRange } from '../../legacy/helpers';
import { SchoolHolidayTerm, SelectedEnrolment, Session, SessionPolicy } from '../../legacy/types';
import { PauseFormValidationSchemaType } from './form-steps/PauseEnrolmentForm/validation';
import {
  calculateAffectedSessions,
  calculatePauseEndDate,
  calculatePauseStartDate,
  calculateResumptionDate,
} from './pauseCalculations';

export type PauseMethodsFactory = (context: PauseEnrolmentContextType) => RadioGroupOption[];
export type PauseResumptionOptionsFactory = (context: PauseEnrolmentContextType) => RadioGroupOption[];
export interface UsePauseEnrolmentOptions {
  createPauseMethods?: PauseMethodsFactory;
  createPauseResumptionOptions?: PauseResumptionOptionsFactory;
}
// Context state interface
export interface PauseEnrolmentState {
  // State variables
  selectedEnrolment: SelectedEnrolment;
  pauseMethod: string | null;
  pauseByWeeksValue: string | null;
  pauseByWeeksRange: { text: number; value: number }[];
  specificPauseStartDate: string | null;
  pauseResumptionMethod: string | null;
  specificResumptionDate: string | null;
  validationErrors: z.ZodFormattedError<PauseFormValidationSchemaType> | null;

  // Derived values
  pauseStartDate: string | null;
  pauseEndDate: string | null;
  affectedSessions: Session[];
  specificResumptionMinDate: moment.Moment;
  specificResumptionMaxDate: moment.Moment | null;
  resumptionDate: string | null;

  // Computed options
  pauseMethods: RadioGroupOption[];
  pauseResumptionOptions: RadioGroupOption[];
}

// Context actions interface
export interface PauseEnrolmentActions {
  setPauseMethod: (method: string | null) => void;
  setPauseByWeeksValue: (value: string | null) => void;
  setSpecificPauseStartDate: (date: string | null) => void;
  setPauseResumptionMethod: (method: string | null) => void;
  setSpecificResumptionDate: (date: string | null) => void;
  setValidationErrors: (errors: z.ZodFormattedError<PauseFormValidationSchemaType> | null) => void;
  clearFieldErrors: (fieldName: keyof PauseFormValidationSchemaType) => void;
  clearAllFieldErrors: () => void;
}

// Combined context type
export type PauseEnrolmentContextType = PauseEnrolmentState &
  PauseEnrolmentActions &
  Omit<PauseEnrolmentProviderProps, 'children'>;

// Create context
export const PauseEnrolmentContext = createContext<PauseEnrolmentContextType | undefined>(undefined);

export type InitialState = {
  pauseMethod?: string | null;
  pauseByWeeksValue?: string | null;
  specificPauseStartDate?: string | null;
  pauseResumptionMethod?: string | null;
  specificResumptionDate?: string | null;
};

const InitialStateDefaults: InitialState = {
  pauseMethod: null,
  pauseByWeeksValue: null,
  specificPauseStartDate: null,
  pauseResumptionMethod: null,
  specificResumptionDate: null,
};

// Provider props
interface PauseEnrolmentProviderProps {
  children: ReactNode;
  currentSchoolTerm: SchoolHolidayTerm;
  currentOrUpcomingHoliday: SchoolHolidayTerm;
  nextSchoolTerm: SchoolHolidayTerm;
  selectedEnrolment: SelectedEnrolment;
  sessions: Session[];
  sessionPolicy: SessionPolicy;
  timezone: string;
  pauseMinDate: moment.Moment;
  pauseMaxDate: moment.Moment;
  initialState: InitialState;
}

// Custom hook for specific resumption date validation
const useSpecificResumptionDate = (
  pauseStartDate: string | null
): [string | null, React.Dispatch<React.SetStateAction<string | null>>] => {
  const [specificResumptionDate, setSpecificResumptionDate] = useState<string | null>(null);

  const validatedSpecificResumptionDate = useMemo(() => {
    if (!specificResumptionDate) {
      return null;
    }
    if (moment(specificResumptionDate).isBefore(moment(pauseStartDate))) {
      return null;
    }
    return specificResumptionDate;
  }, [specificResumptionDate, pauseStartDate]);

  return [validatedSpecificResumptionDate, setSpecificResumptionDate];
};

// Provider component
export const PauseEnrolmentProvider: React.FC<PauseEnrolmentProviderProps> = ({
  children,
  currentSchoolTerm,
  currentOrUpcomingHoliday,
  nextSchoolTerm,
  selectedEnrolment,
  sessions,
  sessionPolicy,
  timezone,
  pauseMinDate,
  pauseMaxDate,
  initialState = InitialStateDefaults,
}) => {
  // State variables
  const [pauseMethod, setPauseMethod] = useState<string | null>(initialState.pauseMethod);

  const [pauseByWeeksValue, setPauseByWeeksValue] = useState<string | null>(initialState.pauseByWeeksValue);
  const pauseByWeeksRange = getPauseRange({ allowedWeeks: sessionPolicy?.pause.allowedWeeks });

  const [specificPauseStartDate, setSpecificPauseStartDate] = useState<string | null>(
    initialState.specificPauseStartDate
  );

  const [pauseResumptionMethod, setPauseResumptionMethod] = useState<string | null>(initialState.pauseResumptionMethod);

  // Derived values
  const pauseStartDate = useMemo(() => {
    return calculatePauseStartDate(pauseMethod, specificPauseStartDate, currentOrUpcomingHoliday);
  }, [pauseMethod, currentOrUpcomingHoliday, specificPauseStartDate]);

  const pauseEndDate = useMemo(() => {
    return calculatePauseEndDate(pauseMethod, pauseStartDate, pauseByWeeksValue, currentOrUpcomingHoliday);
  }, [pauseMethod, pauseStartDate, pauseByWeeksValue, currentOrUpcomingHoliday]);

  const affectedSessions = useMemo(() => {
    return calculateAffectedSessions(
      selectedEnrolment.enrolmentSfid,
      sessions,
      pauseStartDate,
      pauseEndDate,
      getAffectedSessions
    );
  }, [selectedEnrolment.enrolmentSfid, sessions, pauseStartDate, pauseEndDate]);

  const specificResumptionMinDate = useMemo(() => {
    if (!pauseStartDate) {
      return moment(pauseMinDate).add(1, 'day');
    }
    return moment(pauseStartDate).add(1, 'day');
  }, [pauseStartDate, pauseMinDate]);

  const specificResumptionMaxDate = useMemo(() => {
    if (!nextSchoolTerm?.startDate || !moment(nextSchoolTerm?.startDate).isValid()) {
      return null;
    }
    return moment(nextSchoolTerm?.startDate).add(4, 'weeks');
  }, [nextSchoolTerm]);

  const [specificResumptionDate, setSpecificResumptionDate] = useSpecificResumptionDate(pauseStartDate);

  const resumptionDate = useMemo(() => {
    return calculateResumptionDate(
      pauseResumptionMethod,
      nextSchoolTerm,
      specificResumptionDate,
      pauseMethod,
      pauseEndDate
    );
  }, [pauseResumptionMethod, nextSchoolTerm, specificResumptionDate, pauseMethod, pauseEndDate]);

  const [validationErrors, setValidationErrors] = useState<z.ZodFormattedError<PauseFormValidationSchemaType>>(null);

  const clearFieldErrors = useCallback((fieldName: keyof PauseFormValidationSchemaType) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  const clearAllFieldErrors = useCallback(() => {
    setValidationErrors(null);
  }, []);

  useEffect(() => {
    clearAllFieldErrors();
  }, [pauseMethod, pauseResumptionMethod, pauseStartDate, pauseEndDate, clearAllFieldErrors]);

  const contextValue: PauseEnrolmentContextType = {
    // State
    initialState,
    selectedEnrolment,
    pauseMethod,
    pauseByWeeksValue,
    pauseByWeeksRange,
    specificPauseStartDate,
    pauseResumptionMethod,
    specificResumptionDate,
    validationErrors,

    // Derived values
    pauseStartDate,
    pauseEndDate,
    affectedSessions,
    specificResumptionMinDate,
    specificResumptionMaxDate,
    resumptionDate,

    // Pass props
    currentSchoolTerm,
    currentOrUpcomingHoliday,
    nextSchoolTerm,
    sessions,
    sessionPolicy,
    timezone,
    pauseMinDate,
    pauseMaxDate,

    // Computed options
    pauseMethods: [],
    pauseResumptionOptions: [],

    // Actions
    setPauseMethod,
    setPauseByWeeksValue,
    setSpecificPauseStartDate,
    setPauseResumptionMethod,
    setSpecificResumptionDate,
    setValidationErrors,
    clearFieldErrors,
    clearAllFieldErrors,
  };

  return <PauseEnrolmentContext.Provider value={contextValue}>{children}</PauseEnrolmentContext.Provider>;
};

// Custom hook to use the context
export const usePauseEnrolment = (
  options: UsePauseEnrolmentOptions
): PauseEnrolmentContextType & {
  pauseMethods: RadioGroupOption[];
  pauseResumptionOptions: RadioGroupOption[];
} => {
  const context = useContext(PauseEnrolmentContext);
  if (context === undefined) {
    throw new Error('usePauseEnrolment must be used within a PauseEnrolmentProvider');
  }

  // Compute options using the provided factories
  const pauseMethods = useMemo(() => {
    if (options?.createPauseMethods) {
      return options.createPauseMethods(context);
    }
    return []; // fallback empty array
  }, [options, context]);

  const pauseResumptionOptions = useMemo(() => {
    if (options?.createPauseResumptionOptions) {
      return options.createPauseResumptionOptions(context);
    }
    return []; // fallback empty array
  }, [options, context]);

  return {
    ...context,
    pauseMethods,
    pauseResumptionOptions,
  };
};
